// Decides whether the admin phone app registers, removes or is refused the offline-boot
// service worker (BL-2341, ADR 066 — Offline Mode § 1). No React and no globals: the
// registrar component hands it the browser's objects.

import { isOfflineModeBuild } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';

/** The worker script `apps/admin-web/scripts/build-service-worker.ts` writes. */
export const OFFLINE_BOOT_SCRIPT_URL = '/sw.js';

/** The worker's cache-name prefix; must equal the worker's own `CACHE_PREFIX` (an admin-web test pins it). */
export const OFFLINE_BOOT_CACHE_PREFIX = 'offline-boot-';

/**
 * The message asking the worker to confirm the origin answers; must equal the worker's own
 * `CONFIRM_ORIGIN_MESSAGE` (an admin-web test pins it). BUG-2052.
 */
export const OFFLINE_BOOT_CONFIRM_ORIGIN = 'offline-boot:confirm-origin';

/**
 * How long a hand-back waits for the worker's answer before going ahead without it. Longer than
 * the worker's own 3-second check, so a slow origin answers before the page gives up.
 */
export const OFFLINE_BOOT_CONFIRM_TIMEOUT_MS = 4_000;

/**
 * BUG-2160: the cache and key of the app's no-connection mark; must equal the worker's own
 * `NO_CONNECTION_CACHE` / `NO_CONNECTION_KEY` (an admin-web test pins them). While it is set, the
 * worker answers a screen move from the offline document without asking the network.
 */
export const OFFLINE_BOOT_NO_CONNECTION_CACHE = `${OFFLINE_BOOT_CACHE_PREFIX}state`;
export const OFFLINE_BOOT_NO_CONNECTION_KEY = '/__offline-boot-state/no-connection';

/** The slice of `CacheStorage` the no-connection mark uses. */
export interface NoConnectionCachesLike {
    open(name: string): Promise<{ put(key: string, response: Response): Promise<void>; delete(key: string): Promise<boolean> }>;
}

/**
 * Set or clear the app's no-connection mark for the offline-boot worker (BUG-2160). Set only in
 * automatic offline mode, where the app's own probe found no connection. A manual offline mode on a
 * live connection still lets the worker ask the network.
 *
 * @param options.caches - `window.caches`, absent outside a secure context.
 * @param options.noConnection - whether the app knows it has no connection.
 * @returns once the mark is written or removed.
 * @sideEffect Writes or deletes one entry in Cache Storage.
 */
export async function setOfflineBootNoConnection(options: { caches: NoConnectionCachesLike | undefined; noConnection: boolean }): Promise<void> {
    if (!options.caches) return;
    const cache = await options.caches.open(OFFLINE_BOOT_NO_CONNECTION_CACHE);
    if (options.noConnection) await cache.put(OFFLINE_BOOT_NO_CONNECTION_KEY, new Response('1'));
    else await cache.delete(OFFLINE_BOOT_NO_CONNECTION_KEY);
}

/** The slice of `ServiceWorkerContainer` the hand-back uses: the worker controlling this page. */
export interface ServiceWorkerControllerLike {
    controller: { postMessage(message: unknown, transfer: Transferable[]): void } | null;
}

/** What the worker said about the origin: `unknown` when no worker answered. */
export type OfflineBootOriginVerdict = 'reachable' | 'unreachable' | 'unknown';

function verdictOf(data: unknown): OfflineBootOriginVerdict {
    if (typeof data !== 'object' || data === null) return 'unknown';
    const reply = data as { type?: unknown; reachable?: unknown };
    if (reply.type !== OFFLINE_BOOT_CONFIRM_ORIGIN || typeof reply.reachable !== 'boolean') return 'unknown';
    return reply.reachable ? 'reachable' : 'unreachable';
}

/**
 * Ask the offline-boot worker controlling this page whether it reaches the origin. The worker
 * carries the hand-back navigation, and after a failure it serves the offline document for 30
 * seconds without asking the network; a reachable answer ends that window, so the hand-back is not
 * answered with the document it is leaving (BUG-2052). `unknown` when no worker controls the page,
 * or none answers within {@link OFFLINE_BOOT_CONFIRM_TIMEOUT_MS} (an older worker that does not
 * know the message), so the caller can go ahead as before.
 * @param container - `navigator.serviceWorker`, absent when the web view has none.
 * @returns the worker's verdict.
 * @sideEffect Posts a message to the controlling service worker, which requests the probe.
 */
export function confirmOriginWithOfflineBoot(container: ServiceWorkerControllerLike | undefined): Promise<OfflineBootOriginVerdict> {
    const controller = container?.controller;
    if (!controller) return Promise.resolve('unknown');
    return new Promise((resolve) => {
        const channel = new MessageChannel();
        const settle = (verdict: OfflineBootOriginVerdict): void => {
            clearTimeout(timer);
            channel.port1.close();
            resolve(verdict);
        };
        const timer = setTimeout(() => settle('unknown'), OFFLINE_BOOT_CONFIRM_TIMEOUT_MS);
        channel.port1.onmessage = (event: MessageEvent) => settle(verdictOf(event.data));
        try {
            controller.postMessage({ type: OFFLINE_BOOT_CONFIRM_ORIGIN }, [channel.port2]);
        } catch (err) {
            // The hand-back still goes ahead; at worst the worker serves the offline document again.
            // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). Capacitor forwards it to Xcode / logcat.
            console.error('[offline-boot] could not message the service worker', err);
            settle('unknown');
        }
    });
}

/** The slice of `ServiceWorkerRegistration` used here. */
export interface ServiceWorkerRegistrationLike {
    active: { scriptURL: string } | null;
    installing: { scriptURL: string } | null;
    waiting: { scriptURL: string } | null;
    update(): Promise<unknown>;
    unregister(): Promise<boolean>;
}

/** The slice of `ServiceWorkerContainer` used here. */
export interface ServiceWorkerContainerLike {
    register(url: string, options: { scope: string; updateViaCache: 'none' }): Promise<ServiceWorkerRegistrationLike>;
    getRegistrations(): Promise<readonly ServiceWorkerRegistrationLike[]>;
}

/** Everything the decision reads, injected by the registrar. */
export interface OfflineBootRegistrationDeps {
    /** The native-device gateway's answer (ADR 064 — Native Shell § 5); true too for a non-production emulation (BL-2450). */
    isNativeApp: boolean;
    /** This page turned the non-production emulation off (`?emulate-native-app=0`), so its worker goes (BL-2450). */
    emulationTurnedOff: boolean;
    /** {@link isOfflineBootEnabled} for this build. */
    enabled: boolean;
    /** `navigator.serviceWorker`, absent when the web view has none. */
    serviceWorker: ServiceWorkerContainerLike | undefined;
    /** `window.caches`, for the kill switch. */
    caches: { keys(): Promise<string[]>; delete(name: string): Promise<boolean> } | undefined;
    /** `window.isSecureContext`. */
    isSecureContext: boolean;
    log: { info(message: string): void; error(message: string, err: unknown): void };
}

/** What a registration attempt came to. */
export type OfflineBootOutcome =
    | { kind: 'registered' }
    | { kind: 'unregistered'; count: number }
    | { kind: 'skipped'; reason: 'browser' }
    | { kind: 'refused'; reason: 'unsupported' | 'insecure-context' | 'register-rejected' };

/**
 * Whether this build registers the worker: a production build in an offline-mode
 * build (`OFFLINE_MODE_BUILD_ENVS`, the one list the offline transport reads too:
 * local, dev, staging and prod since BL-2448). Off elsewhere; turning it off also
 * removes workers already installed (the kill switch).
 * @param options.auctogonEnv - `NEXT_PUBLIC_AUCTOGON_ENV`.
 * @param options.nodeEnv - `NODE_ENV` (`next dev` has no precacheable build).
 * @returns true when the worker should be registered.
 */
export function isOfflineBootEnabled(options: { auctogonEnv: string | undefined; nodeEnv: string | undefined }): boolean {
    return options.nodeEnv === 'production' && isOfflineModeBuild(options.auctogonEnv);
}

function isOfflineBootRegistration(registration: ServiceWorkerRegistrationLike): boolean {
    const worker = registration.active ?? registration.waiting ?? registration.installing;
    return worker != null && new URL(worker.scriptURL).pathname === OFFLINE_BOOT_SCRIPT_URL;
}

async function removeOfflineBoot(deps: OfflineBootRegistrationDeps, container: ServiceWorkerContainerLike): Promise<OfflineBootOutcome> {
    const ours = (await container.getRegistrations()).filter(isOfflineBootRegistration);
    await Promise.all(ours.map((registration) => registration.unregister()));
    const cacheNames = (await deps.caches?.keys()) ?? [];
    await Promise.all(cacheNames.filter((name) => name.startsWith(OFFLINE_BOOT_CACHE_PREFIX)).map((name) => deps.caches?.delete(name)));
    if (ours.length > 0) deps.log.info(`unregistered ${ours.length} offline-boot worker(s)`);
    return { kind: 'unregistered', count: ours.length };
}

function refuse(deps: OfflineBootRegistrationDeps, reason: 'unsupported' | 'insecure-context' | 'register-rejected', err: unknown): OfflineBootOutcome {
    // On iOS a web view without App-Bound Domains (a store build older than BL-2341) lands here.
    deps.log.error(`service worker refused: ${reason}`, err);
    return { kind: 'refused', reason };
}

/**
 * Register, remove or skip the offline-boot worker for this page.
 * @param deps - The gateway's answer, the build gate and the browser's objects.
 * @returns What happened.
 * @sideEffect Registers or unregisters a service worker and may delete its caches.
 */
export async function runOfflineBootRegistration(deps: OfflineBootRegistrationDeps): Promise<OfflineBootOutcome> {
    const container = deps.serviceWorker;
    if (!deps.isNativeApp) {
        // A browser that just stopped emulating the app drops the worker the emulation registered;
        // any other desktop page never touches the service worker API.
        if (deps.emulationTurnedOff && container) return removeOfflineBoot(deps, container);
        return { kind: 'skipped', reason: 'browser' };
    }
    if (!deps.enabled) return container ? removeOfflineBoot(deps, container) : { kind: 'unregistered', count: 0 };
    if (!container) return refuse(deps, 'unsupported', new Error('navigator.serviceWorker is unavailable'));
    if (!deps.isSecureContext) return refuse(deps, 'insecure-context', new Error('the page is not a secure context'));

    let registration: ServiceWorkerRegistrationLike;
    try {
        registration = await container.register(OFFLINE_BOOT_SCRIPT_URL, { scope: '/', updateViaCache: 'none' });
    } catch (err) {
        return refuse(deps, 'register-rejected', err);
    }
    // An update check each time the app opens picks up a new deploy (AC 3). Offline it
    // fails, which is expected: the installed worker keeps serving.
    try {
        await registration.update();
    } catch (err) {
        deps.log.info(`update check failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    return { kind: 'registered' };
}
